/**
 * SISTEMA DE TEMAS DINÁMICO
 * 
 * Este contexto maneja el sistema de temas claro/oscuro de la aplicación.
 * Proporciona colores específicos para cada sección y transiciones suaves.
 * 
 * ESTRUCTURA DE TEMAS:
 * - bg: Fondos específicos por sección (landing, perfil, experiencia, etc.)
 * - color: Color de texto principal
 * - borderColor: Color de bordes y elementos decorativos
 * 
 * REGLA CRÍTICA: Al añadir nuevas secciones, actualizar AMBOS temas
 * con los fondos correspondientes.
 * 
 * COMPONENTES:
 * - ThemeProviderApp: Provider principal del contexto
 * - ButtonTheme: Componente switch para cambiar tema
 */

import "./buttonTheme.css"
import React, { createContext, useContext, useState } from "react";

export const ThemeContext = createContext(null)

const ThemeProviderApp = ({children}) =>{

    /**
     * TEMA CLARO (LIGHT)
     * 
     * Diseño profesional con fondos blancos y grises claros alternados.
     * Proporciona contraste adecuado y legibilidad óptima.
     */
    const light ={
        bg: {
            landing: "#fff",        // Sección hero - blanco puro
            perfil: "#fff",         // Sobre mí - blanco
            experiencia: "#f8f9fa",  // Experiencia - gris muy claro
            proyectos: "#fff",      // Proyectos - blanco
            education: "#f8f9fa",   // Educación - gris muy claro
            contactos: "#fff",      // Contacto - blanco
            card: "#fff",           // Tarjetas individuales - blanco
            header: "rgba(255, 255, 255, 0.95)", // Header con transparencia
            main: "#fff"            // Fondo principal
        },
        color: "#000",              // Texto negro
        borderColor: "#e2e8f0"      // Bordes grises claros
    };

    /**
     * TEMA OSCURO (DARK)
     * 
     * Diseño elegante con fondos oscuros que reduce fatiga visual.
     * Ideal para visualización nocturna o preferencias de usuario.
     */
    const dark = {
        bg: {
            landing: "#000",        // Sección hero - negro puro
            perfil: "#000",         // Sobre mí - negro
            experiencia: "#1a1a1a", // Experiencia - gris muy oscuro
            proyectos: "#000",      // Proyectos - negro
            education: "#1a1a1a",   // Educación - gris muy oscuro
            contactos: "#000",      // Contacto - negro
            card: "#2d3748",        // Tarjetas - gris medio-oscuro
            header: "rgba(0, 0, 0, 0.95)", // Header con transparencia
            main: "#1a1a1a"        // Fondo principal
        },
        color: "#fff",              // Texto blanco
        borderColor: "#4a5568"      // Bordes grises oscuros
    }

    // Estado del tema actual (inicia en modo claro)
    const[theme,setTheme]= useState(light)

    /**
     * Función para alternar entre temas
     * Detecta el tema actual por el color de fondo del landing
     */
    const toogleTheme = ()=>{
        setTheme(theme.bg.landing === "#fff" ? dark : light);
    }

    return (
    <ThemeContext.Provider value={{theme,toogleTheme}}>
        {children}
    </ThemeContext.Provider>
)}

export default ThemeProviderApp

/**
 * COMPONENTE BOTÓN DE TEMA
 * 
 * Switch personalizado para cambiar entre modo claro y oscuro.
 * Utiliza estilos CSS personalizados definidos en buttonTheme.css
 * 
 * FUNCIONAMIENTO:
 * - Checkbox controlado que refleja el estado del tema
 * - Animaciones suaves definidas en CSS
 * - Detecta tema actual por color de fondo
 */
export const ButtonTheme = () =>{

    const {theme,toogleTheme} = useContext(ThemeContext)

    const handleChange = ()=>{
        toogleTheme()
    }

    return (
        <>
            <label className="switch">
                <input 
                    type="checkbox" 
                    checked={theme.bg.landing==="#fff"?false:true} 
                    id="modeSwitch" 
                    onChange={handleChange}
                />
                <span className="slider"></span>
            </label>
        </>
    )
}